import React, { useCallback, useEffect, useState } from 'react';
import propTypes from 'prop-types';
import cn from 'classnames';

import { PORTRAIT_IMAGE } from '~/Redux/Modules/Admin/Skins';

import { findImagePath, findImagePreviews } from '~/Context/Global';
import { TABLET_WIDTH } from '~/Redux/Constants';

import './CoverGallery.scss';
import Button from '~/Components/Global/Form/Button';

const CoverGallery = props => {
  const { skins, setCurrent } = props;
  const [filteredCovers, setFilteredCovers] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [filter, setFilter] = useState('');
  const [currentIndex, setIndex] = useState(0);
  const [isLoaded, setLoaded] = useState(false);

  const toggling = () => setIsOpen(!isOpen);

  useEffect(() => {
    setFilteredCovers([
      ...skins.sort((a, b) => {
        if (b.priority !== a.priority) {
          return b.priority - a.priority;
        }
        return b.id - a.id;
      }),
    ]);
  }, [skins]);

  const preloadImages = useCallback(() => {
    setLoaded(true);
    //setup image
    let preloader = new Image();
    //set onload
    let index = 0;
    preloader.onload = () => {
      console.log('loaded', skins[index]);
      setIndex(index + 1);
      index += 1;
      if (index >= skins.length) {
        return;
      }
      preloader.src = findImagePath(
        skins[index].images,
        PORTRAIT_IMAGE,
        window.innerWidth <= TABLET_WIDTH
      );
    };

    let imagePreviews = findImagePreviews(
      skins[index].images,
      PORTRAIT_IMAGE,
      window.innerWidth <= TABLET_WIDTH
    );
    let fullImage = findImagePath(
      skins[index].images,
      PORTRAIT_IMAGE,
      window.innerWidth <= TABLET_WIDTH
    );
    preloader.src = imagePreviews['3x'] || fullImage;
  }, []);

  useEffect(() => {
    if (isLoaded) {
      return;
    }
    if (!skins) {
      return;
    }
    preloadImages();
  }, [isLoaded, skins, preloadImages]);

  const filterCategories = {
    all: 'All Covers',
    free: 'Free Covers',
    premium: 'Premium Covers',
    unlocked: 'Unlocked',
  };

  const coverTypeSelected = (category, skins) => {
    setFilteredCovers(
      (() => {
        switch (category) {
          case 'free':
            return [...skins.filter(skin => skin.product.isFree && !skin.product.unlock_code)];
          case 'premium':
            return [...skins.filter(skin => !skin.product.isFree || skin.product.unlock_code)];
          case 'unlocked':
            return [
              ...skins.filter(
                skin => skin.product.purchased && (!skin.product.isFree || skin.product.unlock_code)
              ),
            ];
          default:
            return [...skins.sort((a, b) => b.priority - a.priority || b.id - a.id)];
        }
      })()
    );
  };

  const displayCategories = Object.keys(filterCategories);

  const dropDownMenu = () => {
    return (
      <div className="cover-gallery-filter-dropdown">
        <Button
          text={
            <>
              <span className="material-icons">filter_list</span>{' '}
              {filter ? filterCategories[filter] : 'All Covers'}
            </>
          }
          className="cover-gallery-filter-button"
          onClick={toggling}
        />
        <ul className={`cover-gallery__dropdownlist ${isOpen ? '' : 'hidden'}`}>
          {isOpen &&
            displayCategories.map(key => {
              return (
                <li
                  className="cover-gallery__listitem"
                  onClick={() => {
                    coverTypeSelected(key, skins);
                    toggling();
                    setFilter(key);
                  }}
                >
                  {filterCategories[key]}
                </li>
              );
            })}
          <a
            className="store-link"
            target="_blank"
            role="button"
            href="https://store.artfulagenda.com/collections/premium-covers-stickers"
          >
            <li className="cover-gallery__listitem">
              {
                <>
                  {'Packs'}
                  <span className="material-icons">open_in_new</span>
                </>
              }
            </li>
          </a>
        </ul>
      </div>
    );
  };

  const rowMenu = () => {
    return (
      <div className="cover-gallery-filter-buttons">
        <text>View:</text>
        {displayCategories.map(key => {
          return (
            <Button
              type="submit"
              text={filterCategories[key]}
              className={cn('cover-gallery-filter-button', { clicked: key === filter })}
              onClick={() => {
                coverTypeSelected(key, skins);
                setFilter(key);
              }}
            />
          );
        })}
        <a
          className="store-link"
          target="_blank"
          role="button"
          href="https://store.artfulagenda.com/collections/premium-covers-stickers"
        >
          <Button
            type="submit"
            text={
              <>
                {'Packs'}
                <span className="material-icons">open_in_new</span>
              </>
            }
            className="cover-gallery-filter-button"
          />
        </a>
      </div>
    );
  };

  return (
    <div className="cover-gallery">
      {dropDownMenu()}
      {rowMenu()}
      <ul className="cover-gallery-grid">
        {filteredCovers.map((cover, index) => {
          if (index >= currentIndex) {
            return <li key={index} />;
          }
          let imagePreviews = findImagePreviews(
            cover.images,
            PORTRAIT_IMAGE,
            window.innerWidth <= TABLET_WIDTH
          );
          let fullImage = findImagePath(
            cover.images,
            PORTRAIT_IMAGE,
            window.innerWidth <= TABLET_WIDTH
          );
          return (
            <>
              <li key={`cover/${cover.id}`}>
                <img
                  src={imagePreviews['3x'] || fullImage}
                  alt=""
                  onClick={() => setCurrent(cover)}
                />
              </li>
            </>
          );
        })}
      </ul>
    </div>
  );
};

CoverGallery.propTypes = {
  skins: propTypes.array,
  current: propTypes.bool,
  setCurrent: propTypes.func,
};

export default CoverGallery;
